import { useMemo, useState } from "react";
import { durationSeconds, formatLocalDateKey } from "@if-tracking/shared";
import type { Fast, WeekStart } from "@if-tracking/shared";

interface CalendarHeatmapProps {
  fasts: Fast[];
  timezone: string;
  weekStart: WeekStart;
  /** Notifies the parent which YYYY-MM the user is viewing, for the entries list. */
  onMonthChange?: (yearMonthPrefix: string) => void;
}

type DayState = "none" | "recorded" | "missed";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function CalendarHeatmap({ fasts, timezone, weekStart, onMonthChange }: CalendarHeatmapProps) {
  const todayKey = useMemo(() => formatLocalDateKey(Date.now(), timezone), [timezone]);
  const [year, setYear] = useState(() => Number(todayKey.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(todayKey.slice(5, 7))); // 1-based

  const states = useMemo(() => {
    const map = new Map<string, DayState>();
    for (const fast of fasts) {
      const key = formatLocalDateKey(fast.start_ts, timezone);
      const missed = fast.end_ts !== null && durationSeconds(fast.start_ts, fast.end_ts) < fast.target_seconds;
      if (missed) {
        map.set(key, "missed");
      } else if (map.get(key) !== "missed") {
        map.set(key, "recorded");
      }
    }
    return map;
  }, [fasts, timezone]);

  const firstWeekdayIndex = weekStart === "sun" ? 0 : 1;
  const monthPrefix = `${year}-${pad(month)}`;

  const cells = useMemo(() => {
    const weekdayOfFirst = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    const leadingBlanks = (weekdayOfFirst - firstWeekdayIndex + 7) % 7;
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const out: { key: string; day: number | null; state: DayState; isToday: boolean }[] = [];
    for (let i = 0; i < leadingBlanks; i += 1) {
      out.push({ key: `blank-${i}`, day: null, state: "none", isToday: false });
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      const key = `${monthPrefix}-${pad(d)}`;
      out.push({ key, day: d, state: states.get(key) ?? "none", isToday: key === todayKey });
    }
    return out;
  }, [year, month, firstWeekdayIndex, monthPrefix, states, todayKey]);

  const dow = useMemo(() => {
    const symbols = [...DOW];
    if (weekStart === "mon") symbols.push(symbols.shift() as string);
    return symbols;
  }, [weekStart]);

  const monthLabel = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    year: "numeric"
  }).format(new Date(Date.UTC(year, month - 1, 1)));

  const canGoNext = monthPrefix < todayKey.slice(0, 7);

  function move(delta: number) {
    let nextMonth = month + delta;
    let nextYear = year;
    if (nextMonth < 1) {
      nextMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }
    setYear(nextYear);
    setMonth(nextMonth);
    onMonthChange?.(`${nextYear}-${pad(nextMonth)}`);
  }

  return (
    <div className="calendar">
      <div className="calendar-head">
        <span className="month">{monthLabel}</span>
        <div className="nav">
          <button onClick={() => move(-1)} aria-label="Previous month">
            ‹
          </button>
          <button onClick={() => move(1)} disabled={!canGoNext} aria-label="Next month">
            ›
          </button>
        </div>
      </div>
      <div className="calendar-grid">
        {dow.map((d) => (
          <div className="dow" key={d}>
            {d}
          </div>
        ))}
        {cells.map((cell) =>
          cell.day === null ? (
            <div className="cal-cell blank" key={cell.key} />
          ) : (
            <div className="cal-cell" key={cell.key} style={cell.isToday ? { outline: "1px solid var(--accent)" } : undefined}>
              <span className="num">{cell.day}</span>
              <span className={`dot ${cell.state === "none" ? "spacer" : cell.state}`} />
            </div>
          )
        )}
      </div>
      <div className="cal-legend">
        <span>
          <span className="dot" style={{ background: "var(--accent)" }} /> Met target
        </span>
        <span>
          <span className="dot" style={{ background: "var(--warning)" }} /> Under target
        </span>
      </div>
    </div>
  );
}
